import { useEffect, useRef, useState } from 'react';
import type { ProductPlan } from '@/features/products/types';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import type { CoverageCategoryKey } from '../types';
import adv1 from '../assets/coverage/adv-1.svg';
import adv2 from '../assets/coverage/adv-2.svg';
import adv3 from '../assets/coverage/adv-3.svg';
import adv4 from '../assets/coverage/adv-4.svg';
import reviewerDaniel from '../assets/reviews/daniel.webp';
import reviewerNina from '../assets/reviews/nina.webp';
import reviewerAisha from '../assets/reviews/aisha.webp';
import { useStickyCta } from '../hooks/use-sticky-cta';
import { savingsPercent } from '../lib/savings';
import { OfferStepperHeader } from './offer-stepper-header';
import { OfferAdvantages } from './offer-advantages';
import { OfferPayment } from './offer-payment';
import { OfferDisclaimer } from './offer-disclaimer';
import { OfferReviews } from './offer-reviews';
import { OfferSatisfaction } from './offer-satisfaction';
import { OfferStickyCta } from './offer-sticky-cta';
import { CoveragePayoutCard } from './coverage-payout-card';

const ADVANTAGES = [
  {
    icon: adv1,
    before: 'Get matched against ',
    lead: 'every claim category',
    after: ', not just the basics',
  },
  {
    icon: adv2,
    lead: 'AI scans automatically',
    after: " for eligible settlements you'd otherwise miss",
  },
  {
    icon: adv3,
    before: 'Get alerts the moment a ',
    lead: 'new settlement matches',
    after: ' your profile',
  },
  {
    icon: adv4,
    lead: 'Maximize your total payout',
    after: ' across Health, Environment, and Finance',
  },
];

const REVIEWS = [
  {
    avatar: reviewerDaniel,
    name: 'Daniel K.',
    text: "I had no idea I was even eligible for half of these. Unlocked it and found two more settlements I would've completely missed.",
  },
  {
    avatar: reviewerNina,
    name: 'Nina R.',
    text: 'Thought Corporate was all I qualified for. Turns out I had claims in Health and Finance too — glad I upgraded.',
  },
  {
    avatar: reviewerAisha,
    name: 'Aisha L.',
    text: "Didn't expect much more honestly, but it found stuff in categories I never would've checked myself. Worth it.",
  },
];

const DISCLAIMER =
  'This is a one-time add-on to your MoneyFox plan. No recurring charges. Additional claim categories activate instantly after checkout.';

function scrollToNearestCategories() {
  const nodes = [...document.querySelectorAll<HTMLElement>('[data-coverage-categories]')];
  if (!nodes.length) return;

  const viewCenter = window.innerHeight / 2;
  const nearest = nodes
    .map((node) => {
      const rect = node.getBoundingClientRect();
      return { dist: Math.abs(rect.top + rect.height / 2 - viewCenter), rect };
    })
    .reduce((best, current) => (current.dist < best.dist ? current : best));

  const top = window.scrollY + nearest.rect.top + nearest.rect.height / 2 - viewCenter;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

interface CoverageOfferPageProps {
  offerLabels: string[];
  currentIndex: number;
  available: CoverageCategoryKey[];
  plansByKey: Partial<Record<CoverageCategoryKey, ProductPlan>>;
  purchasing: boolean;
  onUnlock: (keys: CoverageCategoryKey[]) => void;
  onSkip: () => void;
}

export function CoverageOfferPage({
  offerLabels,
  currentIndex,
  available,
  plansByKey,
  purchasing,
  onUnlock,
  onSkip,
}: CoverageOfferPageProps) {
  const { rootRef, stickyHidden } = useStickyCta(currentIndex);
  const firedShown = useRef(false);
  const [selected, setSelected] = useState<Set<CoverageCategoryKey>>(() => new Set());

  useEffect(() => {
    if (firedShown.current) return;
    firedShown.current = true;
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_COVERAGE_SHOWN);
    const plan = available.map((key) => plansByKey[key]).find((p): p is ProductPlan => Boolean(p));
    if (!plan) return;
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: plan.id,
      price: plan.price,
      currency: plan.currency,
      metadata: { kind: 'additional_offer' },
    });
  }, [available, plansByKey]);

  const toggle = (key: CoverageCategoryKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedKeys = available.filter((key) => selected.has(key));
  const selectedPlans = selectedKeys
    .map((key) => plansByKey[key])
    .filter((plan): plan is ProductPlan => Boolean(plan));
  const total = selectedPlans.reduce((sum, plan) => sum + plan.price, 0);
  const previousTotal = selectedPlans.reduce(
    (sum, plan) => sum + (plan.previousPrice ?? plan.price),
    0,
  );
  const currency = selectedPlans[0]?.currency ?? 'USD';
  const discount = savingsPercent(total, previousTotal) ?? undefined;
  const hasSelection = selectedKeys.length > 0;
  const ctaLabel = hasSelection ? 'Unlock More Settlements' : 'Select a category';

  const handleCta = () => {
    if (!hasSelection) {
      scrollToNearestCategories();
      return;
    }
    onUnlock(selectedKeys);
  };

  const payout = () => (
    <CoveragePayoutCard
      extraCount={selectedKeys.length}
      selected={selected}
      available={available}
      onToggle={toggle}
    />
  );

  const payment = (
    <OfferPayment
      total={hasSelection ? total : null}
      previousTotal={hasSelection && previousTotal > total ? previousTotal : undefined}
      currency={currency}
      discountPercent={hasSelection ? discount : undefined}
      ctaLabel={ctaLabel}
      ctaMuted={!hasSelection}
      purchasing={purchasing}
      onCta={handleCta}
    />
  );

  const intro = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
          Unlock <span className="text-mf-green">9,000+ More</span> Settlements
        </h1>
        <p className="font-body text-base font-normal leading-5 text-mf-neutral-600">
          Get matched against every category, including Health, Environment, and Finance.
        </p>
      </div>
      {payout()}
    </div>
  );

  return (
    <div className="min-h-[100dvh] w-full bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div
        ref={rootRef}
        className="mx-auto flex min-h-[100dvh] max-w-app flex-col bg-mf-neutral-50"
      >
        <OfferStepperHeader
          offerLabels={offerLabels}
          currentIndex={currentIndex}
          unlockedLabel="Exclusive Upgrade unlocked"
          onSkip={onSkip}
        />

        <div className="flex flex-col gap-8 px-4 pb-10 pt-6">
          <div className="flex flex-col gap-4">
            {intro()}
            {payment}
          </div>
          <OfferDisclaimer copy={DISCLAIMER} />
          <section className="flex flex-col gap-4">
            <h2 className="text-center font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
              Unlock Full Claim Coverage
            </h2>
            <OfferAdvantages items={ADVANTAGES} />
          </section>
          <OfferReviews
            title={
              <>
                What users say after
                <br />
                unlocking <span className="text-mf-green">Claim Recovery</span>
              </>
            }
            reviews={REVIEWS}
          />
          <div className="h-px w-full bg-mf-neutral-200" />
          <OfferSatisfaction />
          <div className="h-px w-full bg-mf-neutral-200" />
          {intro()}
          {payment}
          <OfferDisclaimer copy={DISCLAIMER} />
        </div>
      </div>

      <OfferStickyCta
        label={ctaLabel}
        onClick={handleCta}
        purchasing={purchasing}
        hidden={stickyHidden}
        muted={!hasSelection}
      />
    </div>
  );
}
